import { createFileRoute, redirect } from '@tanstack/react-router'
import { nodeCanvas } from '@/lib/queries/node-canvases'
import { decodeId, encodeId } from '@/lib/short-uuid'
import { NodeCanvasEditorPage } from '@/features/workspaces/nodes/editor'
import { awaitPendingSave } from '@/features/workspaces/nodes/lib/pending-saves'

function NodeCanvasRouteComponent() {
  const { orgId, workspaceId, canvasId } = Route.useParams()
  return (
    <NodeCanvasEditorPage
      orgId={orgId}
      workspaceId={workspaceId}
      canvasId={canvasId}
    />
  )
}

export const Route = createFileRoute(
  '/_authenticated/org/$orgId/workspace/$workspaceId/nodes/$canvasId'
)({
  staticData: { layout: 'fluid' },
  params: {
    parse: (rawParams: { canvasId: string }) => {
      try {
        return { canvasId: decodeId(rawParams.canvasId) }
      } catch {
        return { canvasId: rawParams.canvasId }
      }
    },
    stringify: (params: { canvasId: string }) => ({
      canvasId: encodeId(params.canvasId),
    }),
  },
  loader: async ({ context, params }) => {
    // The switcher lists the workspace's canvases; opening one must not wait on them.
    void context.queryClient
      .query(nodeCanvas.findManyByWorkspace(params.workspaceId))
      .catch(() => {})
    // The open editor owns the cached row; entering the route must see the
    // server's latest, or a stale copy would overwrite newer edits. An editor
    // just left may still be writing, so that write lands first.
    await awaitPendingSave(params.canvasId)
    const canvas = await context.queryClient.query({
      ...nodeCanvas.findOne(params.canvasId),
      staleTime: 0,
    })
    if (!canvas || canvas.workspace_id !== params.workspaceId) {
      throw redirect({
        to: '/org/$orgId/workspace/$workspaceId/nodes',
        params: { orgId: params.orgId, workspaceId: params.workspaceId },
        replace: true,
      })
    }
  },
  component: NodeCanvasRouteComponent,
})
